/* eslint-disable prefer-arrow-callback */
import axios, { AxiosRequestConfig, ResponseType } from 'axios';
import { cache, getCountryCode, getLang, isEmpty, navigateToLogin } from '@/utils';
import { armsErrorSend } from '@/utils/libs/armsErrorSend';
import { feishuRobotSend } from '@/utils/feishuRobot';
import { errorCode, notSendApis, notSendErrCode } from '@/utils/configs/errorCode.config';
import qs from 'qs';
import { requestDataTransform, requestTransConfig } from './helper/requestTransform';
import { responseDataTransform, responseTransConfig } from './helper/responseTransform';
import { ApiResponse, ApiResponseSuccess, HttpRequestParams } from './typings';

/**
 *
 * @description httpRequest
 * @param {String} options.url 链接
 * @param {String} options.method 请求方法 默认为GET
 * @param {Object} options.data 请求体数据（POST）
 * @param {Object} options.params 与请求一起发送的URL参数，必须是一个简单对象或 URLSearchParams 对象
 * @param {Object} options.headers 自定义header
 * @param {String} options.responseType 自定义responseType
 * @param {Boolean} options.removeInsrc 是否移除分销码
 * @param {Boolean} options.instaToken 是否需要添加 access token 用户鉴权
 * @param {Function} options.onUploadProgress {total,loaded}获取上传进度,（loaded/total).toFixed(1) = 当前上传进度
 * @param {object} option.cancelToken 用于取消请求 cancelToken 传入new axios.CancelToken
 * @param {object} option.serializer 是否需要queryString序列化
 * @link https://axios-http.com/
 * @time 2022/10/24 11:40
 *
 * ---
 * 范型T为响应返回值
 */
function httpRequest<T>(options: HttpRequestParams): Promise<ApiResponse<T>> {
  const {
    url,
    method = 'GET',
    headers = {},
    data = {},
    params = {},
    responseType = 'json',
    onUploadProgress,
    cancelToken,
    serializer = false,
  } = options;

  const axiosConfig: AxiosRequestConfig = {
    url,
    method,
    headers: {
      ...headers,
    },
    data,
    params: {
      'X-Language': options['X_Language'] || getLang() || 'en_us',
      'X-Country': options['X_Country_Code'] || getCountryCode() || 'US',
      ...params,
    },
    responseType: responseType as ResponseType,
    cancelToken,
  };

  // 需要queryString序列化
  if (serializer) {
    axiosConfig.paramsSerializer = (params) => {
      return qs.stringify(params, { encode: true });
    };
  }

  // 预发布token，若有则携带
  const preReleaseToken = window.__NEXT_DATA__?.props?.pageData?.appInject?.preReleaseToken ?? '';
  if (preReleaseToken) {
    axiosConfig.headers!['X-Data-Display-Token'] = preReleaseToken;
  }

  const instaToken = cache.getCookie('insta_access_token');
  if (options.instaToken && instaToken) {
    axiosConfig.headers!['X-User-Token'] = instaToken;
  }

  const insrc = cache.getCookie('insrc');
  if (!options.removeInsrc && insrc) {
    // 将入参转成字符串，避免脏数据导致后端400 BR抛异常
    axiosConfig.params['X-Insrc'] = encodeURIComponent(insrc);
  }

  // 获取进度
  if (onUploadProgress) {
    axiosConfig.onUploadProgress = (progress) => {
      onUploadProgress({ loaded: progress.loaded, total: progress.total });
    };
  }

  // 请求参数转换
  if (requestTransConfig.has(url) && !isEmpty(data)) {
    try {
      const transData = requestDataTransform(url, data);
      axiosConfig.data = transData;
    } catch (err) {
      feishuRobotSend({
        text: ['请求拦截catch error', `【接口url】：${url}`, `【请求config】：${JSON.stringify(axiosConfig)}`],
        err: err as Error,
        at: ['dxy'],
      });
    }
  }

  return new Promise((resolve, reject) =>
    axios(axiosConfig)
      .then(function (response) {
        resolve(response.data);
      })
      .catch(function (error) {
        reject(error);
      })
  );
}

// 请求拦截器
axios.interceptors.request.use(
  function (config) {
    // 发送请求之前转换接口参数数据
    return config;
  },
  function (error) {
    // 对请求错误做些什么
    return Promise.reject(error);
  }
);

// 响应拦截器
axios.interceptors.response.use(
  function (response) {
    try {
      const resDataCode = response?.data?.code;
      // 2xx 范围内的状态码都会触发该函数。
      // 用户鉴权失败
      if (resDataCode === 1) {
        setTimeout(() => {
          // 删除token
          cache.removeCookie('insta_access_token');
          // 鉴权页面需要跳转官网
          if (window.__NEXT_DATA__.props.pageData.runtimeInject.requireAuth) navigateToLogin();
        }, 750);
      }
      const { config = {}, status, data } = response || {};

      if (
        !notSendErrCode.has(data?.code) &&
        status !== 204 &&
        (config?.responseType as HttpRequestParams['responseType']) !== 'html'
      ) {
        const errorInfo = errorCode.get(data?.code);
        const canSend =
          !errorInfo?.notSendAPIs?.some?.((url) => config?.url?.includes?.(url)) &&
          !notSendApis.some?.((url) => config?.url?.includes?.(url));
        const textGroup = [
          `【接口异常响应-错误码异常】 [http状态码: ${status}]-[响应code: ${data?.code}]`,
          `【接口url】：${config?.url}`,
          `【错误码描述】: ${errorInfo?.msg}`,
          `【headers】：${JSON.stringify(config?.headers)}`,
          `【params】：${JSON.stringify(config?.params)}`,
          `【请求参数】：${JSON.stringify(config?.data)}`,
          `【请求config】：${JSON.stringify(config)}`,
          `【响应data】：${JSON.stringify(data ?? '')}`,
        ];

        if (canSend) {
          feishuRobotSend({
            text: textGroup,
            webhook: window.isProd ? 'storeFE' : undefined,
            // at: ['dxy'],
          });
        } else {
          armsErrorSend(
            `接口异常响应-错误码异常-[${status}]-[${data?.code}]: ${response?.config?.url}` as unknown as Error,
            {
              filename: 'axios request',
              data: { textGroup },
            }
          );
        }
      }

      // 如果响应正常，data不为空
      if (responseTransConfig.has(config.url || '') && !isEmpty(data)) {
        try {
          const transData = responseDataTransform(config.url!, data);
          response.data = transData;
        } catch (err) {
          feishuRobotSend({
            text: ['请求拦截catch error', `【接口url】：${config.url}`, `【请求config】：${JSON.stringify(config)}`],
            err: err as Error,
            at: ['dxy'],
          });
        }
      }
    } catch (e) {
      feishuRobotSend({
        text: [
          `【接口异常响应-错误码异常-catchError】 [http状态码: ${response?.status}]`,
          `【请求config】：${JSON.stringify(response?.config)}`,
          `【响应data】：${JSON.stringify(response?.data ?? '')}`,
        ],
        err: e as Error,
        at: ['dxy'],
      });
    }

    return response;
  },
  function (error) {
    try {
      // 1. 当请求返回的 HTTP 状态码不在 2xx 范围内时，即请求失败。
      // 2. 当请求发送时出现错误，例如网络错误或超时。errorMsg: "Error: Network Error" || errorMsg: "Error: timeout exceeded",
      const errorMsg = error?.toString() ?? '';

      // 过滤网络错误和超时
      const ignoreErrMsg = ['Network Error', 'timeout exceeded', 'Request aborted'];
      if (ignoreErrMsg.find((o) => errorMsg.includes(o))) return Promise.reject(error);

      // 过滤爬虫403错误
      const ignoreBots = ['bingbot'];
      if (
        ignoreBots.find((o) => window?.navigator?.userAgent && window?.navigator?.userAgent.includes(o)) &&
        error.response.status === 403
      )
        return Promise.reject(error);

      const { response = {}, config: requestConfig = {} } = error || {};
      // 429-too many requests
      const canSend =
        response?.status &&
        window.location.hostname?.includes('insta360.com') &&
        ![429].includes(response?.status) &&
        !notSendApis.some?.((url) => requestConfig?.url?.includes?.(url));

      if (canSend) {
        feishuRobotSend({
          text: [
            `【接口异常响应-状态码异常】： ${response?.status}`,
            `【接口url】：${requestConfig?.url}`,
            `【method】：${requestConfig?.method}`,
            `【headers】：${JSON.stringify(requestConfig?.headers)}`,
            `【params】：${JSON.stringify(requestConfig?.params)}`,
            `【请求参数】：${JSON.stringify(requestConfig?.data)}`,
            `【请求config】：${JSON.stringify(requestConfig)}`,
            `【响应data】：${JSON.stringify(response?.data)}`,
          ],
          err: error,
          at: window.isProd ? ['dxy'] : undefined,
        });
      }

      return Promise.reject(error);
    } catch (e) {
      feishuRobotSend({
        text: [
          `【接口异常响应-状态码异常-catchError】${error?.response?.status}`,
          `【状态码异常请求errorConfig】：${JSON.stringify(error?.config)}`,
          `【响应data】：${JSON.stringify(error?.response?.data)}`,
        ],
        err: e as Error,
        at: ['dxy'],
      });
      return Promise.reject(error);
    }
  }
);

/**
 * 请求中某个字段限制50个
 * @param {object} httpRequestOptions httpRequest请求的options
 * @param {string} limitKey 限制的字段，由httpRequestOptions.data中获取，默认commodity_ids
 * @param {number} number 请求字段的最大数量，默认50个
 *
 * @return {Promise} res {code,data} 只要有一个请求成功则返回成功请求，否则视为整个失败
 * data的返回值必须是key:数组，{xxx:[数组]}，不能是对象。否则没有必要限制数量了
 *
 * T：响应的data类型，限制必须是{xxx:[数组]}
 *
 */
function httpRequestLimitNums<T extends Record<string, unknown[]> = Record<string, unknown[]>>(
  options: HttpRequestParams<Record<string, unknown>>,
  limitKey: string = 'commodity_ids',
  number: number = 50
): Promise<ApiResponse<T>> {
  if (isEmpty(options.data?.[limitKey])) {
    return httpRequest<T>(options);
  }

  const ids = options.data?.[limitKey] as number[];
  const keys = [];
  for (let i = 0; i < Math.ceil(ids.length / number); i++) {
    keys.push(ids.slice(i * number, (i + 1) * number));
  }

  const httpRequests = keys.map((_keys) => httpRequest({ ...options, data: { ...options.data, [limitKey]: _keys } }));
  const isAllSettled = !!Promise.allSettled;
  const PromiseAll = isAllSettled ? Promise.allSettled(httpRequests) : Promise.all(httpRequests);

  return PromiseAll.then(
    (argus) => {
      // 错误校验返回
      if (
        isAllSettled
          ? (argus as PromiseSettledResult<ApiResponse<T>>[]).every(
              (argument) =>
                argument.status === 'rejected' || (argument.status === 'fulfilled' && argument?.value?.code !== 0)
            )
          : (argus as ApiResponse<T>[]).every((argument) => !argument || argument?.code !== 0)
      ) {
        armsErrorSend('接口异常响应拦截：httpRequestLimitNums 所有请求失败' as unknown as Error, {
          filename: 'httpRequestLimitNums',
          data: { options, argus, isAllSettled },
        });
        return { code: -1 };
      }

      const res: ApiResponseSuccess<Record<PropertyKey, unknown[]>> = { code: 0, data: {} };

      if (isAllSettled) {
        (argus as PromiseSettledResult<ApiResponseSuccess<T>>[]).forEach((_res) => {
          if (_res.status === 'rejected') return;
          const { code, data } = _res?.value || {};

          if (code === 0 && data) {
            Object.keys(data).forEach((key) => {
              if (Array.isArray(data[key])) {
                res.data![key] = [...(res.data![key] || []), ...data[key]];
              }
            });
          }
        });
      } else {
        (argus as ApiResponse<T>[]).forEach((_res) => {
          const { code, data } = _res || {};

          if (code === 0 && data) {
            Object.keys(data).forEach((key) => {
              if (Array.isArray(data[key])) {
                res.data![key] = [...(res.data![key] || []), ...data[key]];
              }
            });
          }
        });
      }

      return res as ApiResponseSuccess<T>;
    },
    (e) => {
      armsErrorSend(e, { filename: 'httpRequestLimitNums', data: options });
      return { code: -1 };
    }
  ).catch((e) => {
    armsErrorSend(e, { filename: 'httpRequestLimitNums', data: options });
    return { code: -1 };
  });
}

export { httpRequest, httpRequestLimitNums };
